import { isEmpty } from "ramda";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Typography } from "@/components/atoms";
import { DataTable, EmptyState } from "@/components/molecules";
import { useProfile } from "@/hooks";
import { useAppSelector } from "@/redux/hooks";
import { companiesPageSelector } from "@/redux/slices/company/selectors";
import { Company } from "@/redux/slices/company/types";
import { PricingProfile } from "@/redux/slices/trip/types";
import { getPaginationLabel } from "../helpers";
import { RemoveCompanyModal } from "./RemoveCompanyModal";
import { SearchAndAttachCompanyModal } from "./SearchAndAttachCompanyModal";
import { generateCompaniesColumns, generateCompaniesData } from "./helpers";
import { CompaniesAttachedAction } from "./types";

interface CompaniesAttachedTableProps extends React.HTMLAttributes<HTMLDivElement> {
  companies: Company[];
  profile?: PricingProfile;
}

export const CompaniesAttachedTable = ({ companies, profile, className, ...props }: CompaniesAttachedTableProps) => {
  const navigate = useNavigate();
  const { dateFormat } = useProfile();
  const { current, size, total } = useAppSelector(companiesPageSelector);
  const [action, setAction] = useState<CompaniesAttachedAction>(undefined);
  const [selectedCompany, setSelectedCompany] = useState<Company | undefined>(undefined);

  const handleRemove = (company: Company) => {
    setSelectedCompany(company);
    setAction("removeCompany");
  };

  const data = useMemo(
    () => generateCompaniesData(companies, handleRemove, dateFormat),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companies]
  );

  const columns = useMemo(
    () => generateCompaniesColumns(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className={className} {...props}>
      <div className="mb-4 flex flex-col justify-between gap-4 xl:flex-row">
        <div className="flex flex-col gap-1">
          <Typography variant="h2">{`Companies Attached (${data.length})`}</Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            Here is the list of companies this pricing profile is attached to. You can set up a new company in the
            <span className="cursor-pointer text-info" onClick={() => navigate("/companies")}>
              {" "}
              Company{" "}
            </span>
            section.
          </Typography>
        </div>
        {!isEmpty(data) && (
          <div className="flex">
            <Button variant="primary" size="sm" startIcon="SearchNormal1" onClick={() => setAction("searchAndAttachCompany")}>
              Search and Attach Company
            </Button>
          </div>
        )}
      </div>
      {isEmpty(data) ? (
        <div className="rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray">
          <EmptyState
            title="Nothing here… yet"
            description="You haven't attached anything to this pricing profile. Search a company to attach."
          >
            <Button variant="primary" size="sm" startIcon="SearchNormal1" onClick={() => setAction("searchAndAttachCompany")}>
              Search and Attach Company
            </Button>
          </EmptyState>
        </div>
      ) : (
        <>
          <DataTable columns={columns} data={data} className="w-full overflow-x-auto rounded-b-none bg-white [&_table]:table-auto" />
          <div className="flex w-full items-center justify-between rounded-b-lg bg-neutral-surface-gray p-2 ring-1 ring-neutral-mid-gray">
            <Typography variant="small-regular" className="text-neutral-silver-gray">
              {getPaginationLabel(current, size, total)}
            </Typography>
          </div>
        </>
      )}
      <RemoveCompanyModal
        open={action === "removeCompany"}
        onClose={() => {
          setAction(undefined);
          setSelectedCompany(undefined);
        }}
        selectedCompany={selectedCompany}
      />
      {profile && (
        <SearchAndAttachCompanyModal open={action === "searchAndAttachCompany"} onClose={() => setAction(undefined)} profile={profile} />
      )}
    </div>
  );
};
